import { MutationError, PlatformSource } from '@/api/types/common.types';
import {
  AdFormFields,
  EditAdFormFields,
  PhotoFieldType,
  AdFormError,
} from '@/components/AdForm/AdForm.types';
import {
  AdDetails,
  ContactType,
  CreateAdAttributes,
  UpdateAdAttributes,
} from '@/api/types/ads.types';
import { PropertyOption, PropertyType } from '@/api/types/categories.types';
import { getFlowId } from '@/hooks/useAdSubmissionFlow/useAdSubmissionFlow.helpers';

const ignoredPaths = [
  'user',
  'contact.phones_separated',
  'category',
  'category_unscoped',
];

function normalizeErrorPath(rawPath?: string[]): string | undefined {
  if (!rawPath) return;

  const path = rawPath.join('.').replace(/\//g, '.');

  if (ignoredPaths.includes(path)) return;

  if (path.includes('category_path')) return 'categoryId';
  if (path.includes('contact.phones')) return 'contact.phones.0';
  if (path.includes('region')) return 'regionId';
  if (path.includes('photo')) return 'photoIds';
  if (path.startsWith('property_set.'))
    return path.replace('property_set.', '');

  return path;
}

export function mapApiErrorsToFormFields<T extends AdFormError>(
  errors: MutationError[]
): T[] {
  return errors.reduce<T[]>((formErrors, mutationError) => {
    const path = normalizeErrorPath(mutationError.path);

    if (!path) return formErrors;

    const isExists = formErrors.find(error => error.name === path);

    const { message, extensions } = mutationError;

    return isExists
      ? formErrors
      : [...formErrors, { name: path, message, extensions } as T];
  }, []);
}

export function buildCreateAdAttributes(
  data: AdFormFields,
  source: PlatformSource
): CreateAdAttributes {
  const {
    categoryId,
    title,
    regionId,
    body,
    price,
    photoIds,
    contact,
    collection,
    boolean,
    range,
  } = data;
  const parsedPrice = parseFloat(price.replace(',', '.'));
  return {
    categoryId,
    title,
    regionId,
    body,
    price: parsedPrice >= 0 ? parsedPrice : null,
    photoIds: photoIds?.map(({ id }) => id),
    source,
    contactAttributes: {
      contactType: contact.contactType || ContactType.CallsAndMessages,
      email: contact.email || '',
      name: contact.name || '',
      phones: [contact.phones?.[0] || ''],
      ...(contact.jwt && { jwt: contact.jwt }),
    },
    propertySet: {
      collection: Object.entries(collection).reduce<
        { legacyId: string; value: string }[]
      >((acc, [legacyId, rawValue]) => {
        if (rawValue === undefined) return acc;
        const value =
          (rawValue as PropertyOption)?.legacyResourceId ?? rawValue ?? '';
        return [...acc, { legacyId, value }];
      }, []),
      boolean: Object.entries(boolean).map(([legacyId, value]) => ({
        legacyId,
        value: value || false,
      })),
      range: Object.entries(range).reduce<
        { legacyId: string; value: number }[]
      >((acc, [legacyId, rawValue]) => {
        const value = parseFloat((rawValue ?? '').replace(',', '.'));
        return Number.isNaN(value) ? acc : [...acc, { legacyId, value }];
      }, []),
    },
    flowId: getFlowId() || undefined,
  };
}

export function buildUpdateAdAttributes(
  data: EditAdFormFields
): UpdateAdAttributes {
  const { body, price, photoIds, contactType, boolean } = data;
  const parsedPrice = parseFloat(price.replace(',', '.'));
  return {
    body,
    contactType,
    price: parsedPrice >= 0 ? parsedPrice : null,
    photoIds: photoIds?.map(({ id }) => id),
    propertySet: {
      boolean: Object.entries(boolean).map(([legacyId, value]) => ({
        legacyId,
        value: value || false,
      })),
    },
  };
}

export function checkOptionsAutogeneratedTitle(
  collection: AdFormFields['collection']
) {
  return Object.values(collection).every(
    option => typeof option !== 'object' || option.hasAutogeneratedTitle
  );
}

export function getPhotoFieldType(option: PropertyOption) {
  if (option.photos === null) {
    return PhotoFieldType.UploadPhoto;
  }

  if (option.photos.length > 0) {
    return PhotoFieldType.PickPresetPhoto;
  }

  return PhotoFieldType.Hidden;
}

export function scrollToFirstError(errors: AdFormError[]) {
  if (!errors || errors.length === 0) return;

  const fieldNamesSet = new Set<string>(errors.map(({ name }) => name));
  const elements = document.querySelectorAll<HTMLElement>('[name]');

  for (const element of elements) {
    const name = element.getAttribute('name');
    if (name && fieldNamesSet.has(name)) {
      const errorMessage = element.querySelector('#helper-text-container');
      const targetElement = errorMessage || element;
      targetElement.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      });
      return;
    }
  }
}

export function getInitialValuesForEdit(
  initialAdDetails: AdDetails | null
): EditAdFormFields {
  const adData = initialAdDetails?.pendingEdit || initialAdDetails;

  return {
    price: adData?.price?.toString() || '',
    contactType:
      initialAdDetails?.pendingEdit?.contactType ||
      initialAdDetails?.contact?.type ||
      ContactType.CallsAndMessages,
    boolean:
      adData?.properties?.reduce<Record<string, boolean>>((acc, property) => {
        if (property.type !== PropertyType.BooleanProperty) return acc;
        return {
          ...acc,
          [property.legacyResourceId]: property.originalValue.value as boolean,
        };
      }, {}) ?? {},
    body: adData?.body || '',
    photoIds:
      adData?.photos?.map(({ id, url }) => ({
        id,
        src: url,
      })) || [],
  };
}
